export type ChatRecord = Record<string, unknown>;
export type MessageStatus = "sending" | "sent" | "delivered" | "read" | "failed";

export interface ChatMessage {
  _id: string;
  chatId: string;
  senderId: string;
  content: string;
  images?: string[];
  video?: string;
  documents?: { url: string; name: string }[];
  time: string;
  timestamp: Date;
  sent: boolean;
  type: "text" | "image" | "video" | "file";
  replyTo?: string;
  isUploading?: boolean;
  isOptimistic?: boolean;
  status?: MessageStatus;
  isEdited?: boolean;
  clientKey?: string;
}

export interface UserConversation {
  _id: string;
  name: string;
  participantId: string;
  lastMessage: string;
  time: string;
  timestamp: Date;
  unread: number;
  avatar?: string;
  online?: boolean;
  pinned: boolean;
  unavailable?: boolean;
}

export const record = (value: unknown): ChatRecord =>
  value && typeof value === "object" && !Array.isArray(value) ? value as ChatRecord : {};

export const entityId = (value: unknown): string => {
  if (typeof value === "string") return value;
  const data = record(value);
  return typeof data._id === "string" ? data._id : typeof data.id === "string" ? data.id : "";
};

export const messageRoom = (value: unknown): string => {
  const data = record(value);
  return entityId(data.chatId) || entityId(data.chat);
};

const text = (value: unknown): string => typeof value === "string" ? value : "";
const mediaUrl = (value: unknown): string => typeof value === "string" ? value : text(record(value).url);

export const validDate = (value: unknown): Date => {
  const result = new Date(typeof value === "string" || typeof value === "number" ? value : 0);
  return Number.isNaN(result.getTime()) ? new Date(0) : result;
};

export const clockTime = (date: Date): string => date.getTime() ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

export const normalizeMessage = (value: unknown, accountId: string, chatId: string): ChatMessage | null => {
  const data = record(value);
  const id = entityId(data);
  const senderId = entityId(data.sender) || entityId(data.senderId);
  const room = messageRoom(data);
  if (!id || !senderId || (room && room !== chatId)) return null;
  if (data.deletedFor === "everyone" || data.deletedFor === "all" || data.deletedFor === "me" || data.isDeleted === true) return null;
  const files = Array.isArray(data.files) ? data.files.map(record) : [];
  const images = [...(Array.isArray(data.images) ? data.images.map(mediaUrl) : []), ...files.filter(file => file.type === "image" || text(file.mimeType).startsWith("image/")).map(mediaUrl)].filter(Boolean);
  const video = mediaUrl(data.video) || mediaUrl(files.find(file => file.type === "video" || text(file.mimeType).startsWith("video/")));
  const documents = [...(Array.isArray(data.documents) ? data.documents : []), ...files.filter(file => file.type === "document" || file.type === "file")].map(file => ({ url: mediaUrl(file), name: text(record(file).name) || text(record(file).originalName) || "Document" })).filter(file => file.url);
  const timestamp = validDate(data.createdAt || data.timestamp);
  const status = data.status === "read" || data.status === "delivered" || data.status === "sent" ? data.status : "sent";
  return { _id: id, chatId, senderId, content: text(data.content) || text(data.text), images: images.length ? [...new Set(images)] : undefined, video: video || undefined, documents: documents.length ? documents : undefined, time: clockTime(timestamp), timestamp, sent: senderId === accountId, type: images.length ? "image" : video ? "video" : documents.length ? "file" : "text", replyTo: entityId(data.replyTo) || undefined, status, isEdited: data.isEdited === true };
};

export const messageFromResponse = (value: unknown): unknown => {
  const data = record(value);
  return data.message || data.updated || record(data.data).message || (entityId(data.data) ? data.data : entityId(data) ? data : null);
};

export const mergeMessages = (current: ChatMessage[], incoming: ChatMessage[], replaceId?: string): ChatMessage[] => {
  const byId = new Map(current.filter(message => message._id !== replaceId).map(message => [message._id, message]));
  incoming.forEach(message => {
    const old = byId.get(message._id);
    const replaced = replaceId ? current.find(item => item._id === replaceId) : undefined;
    byId.set(message._id, { ...old, ...message, clientKey: old?.clientKey || replaced?.clientKey || message.clientKey });
  });
  return [...byId.values()].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime() || a._id.localeCompare(b._id));
};

/** Fresh history owns unchanged cache entries; only changes after request start
 * and outstanding optimistic sends may override that authoritative snapshot. */
export const reconcileHistory = (history: ChatMessage[], current: ChatMessage[], snapshot: ReadonlyMap<string, ChatMessage>): ChatMessage[] => {
  const changed = current.filter(message => message.isOptimistic || snapshot.get(message._id) !== message);
  return mergeMessages(history, changed);
};

export const preview = (message: Pick<ChatMessage, "content" | "images" | "video" | "documents">): string =>
  message.content || (message.images?.length ? "Photo" : message.video ? "Video" : message.documents?.length ? "Document" : "No messages yet");

export const errorMessage = (error: unknown, fallback: string): string => {
  const data = record(error);
  return text(record(record(data.response).data).message) || text(data.message) || fallback;
};

export const conversationTime = (date: Date): string => {
  if (!date.getTime()) return "";
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return clockTime(date);
  const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
  return date.toDateString() === yesterday.toDateString() ? "Yesterday" : date.toLocaleDateString([], { month: "short", day: "numeric" });
};
